import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "index";

import { useTheme, Stack, useMediaQuery, Box, Checkbox } from "@mui/material";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";

import { configSidebar } from "@store/options/actions";
import { deselectCategories, selectCategories, setTimelineSelection } from "@store/filters/actions";
import { Item, OptionKey, VisTypes } from "@store/options/types";
import { CategoryFilterSelector } from "./CategoryFilterSelector";
import DataExportSelector from "./DataExportSelector";
import OptionItem from "./OptionItem";
import { Upload as UploadIcon } from "@mui/icons-material";
import useTranslations from "containers/hooks/useLanguageHook";
import { ColorToggleItem, FilterType } from "@store/filters/types";
import { Theme } from "@mui/system";
import ContactFilter from "./ContactFilter";
import TimelineSelector from "containers/Visuals/TimelineSelector";
import NavBarButton from "containers/NavbarComponents/NavBarButton";

function ConfigSidebar() {
  const activeFilters = useAppSelector((state) => state.filters.active);
  const vis = useAppSelector((state) => state.options.currentVis);
  const options = useAppSelector((state) => state.options.options);
  const active = useAppSelector((state) => state.options.configSidebarOpen);
  const categoryFilters = useAppSelector((state) => state.filters.categories);
  const filetypeFilters = useAppSelector((state) => state.filters.fileTypes);
  const { t } = useTranslations();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const currentVisOptions = Object.entries<Item>(options).filter(
    ([, d]) => d.view.includes(vis) && !d.view.includes(VisTypes.All),
  );
  const globalOptions = Object.entries<Item>(options).filter(
    ([, d]) => d.view.includes(VisTypes.All) && !d.view.includes(vis),
  );

  const breakpoint = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));

  useEffect(() => {
    if (!breakpoint) dispatch(configSidebar(false));
    // if (breakpoint && !active) dispatch(configSidebar(true));
  }, [breakpoint]);

  function renderFilterSection(type: FilterType, filter: ColorToggleItem[], title: string) {
    const selectedFilters = filter.filter((x) => x.value).length;
    const checkboxState = selectedFilters !== 0;
    const checkboxStateIndeterminate = selectedFilters !== 0 && selectedFilters !== filter.length;

    return (
      <Box mb={2}>
        <Stack
          direction="row"
          sx={{
            display: "grid",
            gridTemplateRows: 1,
            justifyItems: "center",
            mb: 1,
          }}
        >
          <Checkbox
            edge="start"
            onChange={(e) => {
              if (e.target.checked) {
                dispatch(selectCategories(type));
              } else {
                dispatch(deselectCategories(type));
              }
            }}
            checked={checkboxState}
            indeterminate={checkboxStateIndeterminate}
            sx={{
              justifySelf: "start",
              pt: 1.5,
              pl: 3,
            }}
          />
          <Typography align="center" sx={{}}>
            {title}
          </Typography>
        </Stack>
        <CategoryFilterSelector items={filter} type={type} />
      </Box>
    );
  }

  return (
    <Drawer
      PaperProps={{
        sx: {
          width: theme.sidebar.config.width,
        },
      }}
      sx={{
        width: theme.sidebar.config.width,
        flexGrow: 1,
      }}
      variant="persistent"
      open={active}
      anchor="right"
    >
      <Box
        sx={{
          display: "flex",
          height: "100%",
          flexDirection: "column",
          justifyContent: "start",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            overflowX: "hidden",
            overflowY: "scroll",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              alignSelf: "center",
              textAlign: "center",
              fontWeight: 500,
              padding: 1,
            }}
          >
            {t.Vis.DataExportsTitle}
          </Typography>
          <DataExportSelector />
          <Divider />
          <Typography
            variant="h5"
            sx={{
              alignSelf: "center",
              textAlign: "center",
              fontWeight: 500,
              padding: 1,
            }}
          >
            {t.Vis.TimeRangeSelection}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "Row",
              justifyContent: "center",
            }}
          >
            <TimelineSelector
              settings={{
                bins: 20,
                padding: { left: 6, top: 10, right: 6, bottom: 20 },
                xAxisTicks: 5,
              }}
              onSelected={(e) => {
                if (e) dispatch(setTimelineSelection(e));
              }}
              size={{ width: "95%" }}
            />
          </Box>
          <Box>
            <Divider />
            <Typography
              variant="h5"
              sx={{
                alignSelf: "center",
                textAlign: "center",
                fontWeight: 500,
                padding: 1,
              }}
            >
              {t.Vis.VisSettingsTitle}
            </Typography>
            <List>
              {currentVisOptions.map(([key, d]) => (
                <OptionItem key={key} name={key as OptionKey} item={d} />
              ))}
            </List>
          </Box>
          {activeFilters.some((d) => FilterType.messageCount === d) && <ContactFilter title={t.Vis.FilterBoxTitle} />}
          {activeFilters.some((d) => FilterType.fileTypes === d) &&
            renderFilterSection(FilterType.fileTypes, filetypeFilters, t.Vis.FileTypeFilterTitle)}
          {activeFilters.some((d) => FilterType.categories === d) &&
            renderFilterSection(FilterType.categories, categoryFilters, t.Vis.CategoriesFilterTitle)}
          {
            // TODO: there are currently also other filtertypes "provider" "subcategory" but they are not supported
          }
          <Divider />
          <Typography
            variant="h5"
            sx={{
              alignSelf: "center",
              textAlign: "center",
              fontWeight: 500,
              padding: 1,
            }}
          >
            {t.Vis.GlobalSettingsTitle}
          </Typography>
          <List>
            {globalOptions.length === 0 ? (
              <ListItem>
                <ListItemText secondary={"No Options available"} />
              </ListItem>
            ) : (
              globalOptions.map(([key, d]) => <OptionItem key={key} name={key as OptionKey} item={d} />)
            )}
          </List>
        </Box>

        <NavBarButton
          sx={{
            flexGrow: 0,
            flexShrink: 0,
            height: theme.sidebar.bottomBarHeight,
          }}
          icon={
            active ? (
              <UploadIcon sx={{ transform: "rotate(90deg)" }} />
            ) : (
              <UploadIcon sx={{ transform: "rotate(-90deg)" }} />
            )
          }
          text={active ? t.UI.Collapse : t.UI.Expand}
          onClick={() => dispatch(configSidebar())}
        />
      </Box>
    </Drawer>
  );
}

export default ConfigSidebar;
